import { forwardRef } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert, type AlertLive } from '../atoms/Alert/Alert';
import { getAiLimitErrorKind, getAiLimitResetsAt } from '../../services/aiUsageService';
import type { AiLimitErrorKind } from '../../services/aiUsageService';

/** Formats a reset timestamp as a short day + month ("1 Nov"), in UTC like the budget month. */
export function formatResetDate(iso: string | null | undefined, language: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date);
}

export interface AiLimitNoticeProps {
  kind: AiLimitErrorKind;
  /** Next reset (ISO) — shown for 'exceeded'. */
  resetsAt?: string | null;
  /** Announcement politeness (see Alert). Defaults to assertive. */
  live?: AlertLive;
  /** Makes the notice programmatically focusable (focus is moved to it after a failed action). */
  tabIndex?: number;
}

/**
 * Explains why AI features are unavailable: the user's monthly AI credits are
 * used up (with the reset date and a pointer to support — there are no plans or
 * top-ups to buy, see src/config/plans.ts) or the app-wide
 * AI budget is temporarily exhausted.
 */
export const AiLimitNotice = forwardRef<HTMLDivElement, AiLimitNoticeProps>(function AiLimitNotice(
  { kind, resetsAt, live, tabIndex },
  ref,
) {
  const { t, i18n } = useTranslation();

  if (kind === 'unavailable') {
    return (
      <Alert ref={ref} tone="warning" live={live} tabIndex={tabIndex} className="rg-ai-limit-notice">
        {t('aiUsage.unavailable')}
      </Alert>
    );
  }

  const date = formatResetDate(resetsAt, i18n.language);
  return (
    <Alert
      ref={ref}
      tone="warning"
      live={live}
      tabIndex={tabIndex}
      className="rg-ai-limit-notice"
      title={date !== null ? t('aiUsage.exceeded', { date }) : t('aiUsage.exceededNoDate')}
    >
      {t('aiUsage.supportHint')}
    </Alert>
  );
});

export interface AiLimitErrorMessageProps {
  error: unknown;
  /** Makes the notice programmatically focusable (see AiLimitNotice). */
  tabIndex?: number;
  /**
   * Render nothing for AI_BUDGET_EXCEEDED — set where an AiUsageMeter next to
   * the feature already explains the used-up credits (avoids a second alert).
   */
  suppressExceeded?: boolean;
}

/** Renders AiLimitNotice for an AI_BUDGET_EXCEEDED / AI_TEMPORARILY_UNAVAILABLE error, else nothing. */
export const AiLimitErrorMessage = forwardRef<HTMLDivElement, AiLimitErrorMessageProps>(function AiLimitErrorMessage(
  { error, suppressExceeded = false, tabIndex },
  ref,
) {
  const kind = getAiLimitErrorKind(error);
  if (kind === null || (kind === 'exceeded' && suppressExceeded)) return null;
  return <AiLimitNotice ref={ref} kind={kind} resetsAt={getAiLimitResetsAt(error)} tabIndex={tabIndex} />;
});

export default AiLimitNotice;
